import { Tooltip } from "@fronterahealth/frontera-ui-components";

interface StatusDefinition {
  qualifier?: string;
  description: string;
}

// Single source of truth for the report-status pill descriptions. Used by the
// table-header legend and by the per-pill hover tooltip in the report view.
// Keys match the lifecycle StatusType values returned by getReportLifecycleStatus.
export const STATUS_DEFINITIONS: Record<string, StatusDefinition> = {
  Draft: {
    description: "This report is still in progress and hasn't been completed yet.",
  },
  Complete: {
    description:
      "This report has been finished. For most reports, no further action is needed. If this report is linked to a payor integration, it's ready to submit.",
  },
  Submitted: {
    qualifier: "Payor-integrated reports only",
    description: "This report has been sent to your payor network and is currently under review.",
  },
  "Needs Revision": {
    qualifier: "Payor-integrated reports only",
    description:
      "Your payor has reviewed this report and returned feedback. Revisions are required before resubmitting.",
  },
  Approved: {
    qualifier: "Payor-integrated reports only",
    description: "Your payor has approved this report. Goals and targets are ready to sync to your EHR.",
  },
};

const LEGEND_ORDER: Array<keyof typeof STATUS_DEFINITIONS> = [
  "Draft",
  "Complete",
  "Submitted",
  "Needs Revision",
  "Approved",
];

// Small info-icon tooltip rendered next to the Status column header. Hover
// surfaces a glossary of all status values so clinicians can self-serve the
// meaning without needing the spec doc.
export const StatusLegendTooltip: React.FC = () => (
  <Tooltip
    placement="bottom"
    infoIconClassName="!h-4 !w-4"
    text={
      <ul className="space-y-3 py-1 text-left">
        {LEGEND_ORDER.map((label) => {
          const def = STATUS_DEFINITIONS[label];
          return (
            <li key={label}>
              <div className="text-xs font-semibold leading-tight">
                {label}
                {def.qualifier ? <span className="ml-1 font-normal text-limestone-300">({def.qualifier})</span> : null}
              </div>
              <div className="text-xs leading-snug">{def.description}</div>
            </li>
          );
        })}
      </ul>
    }
  />
);
